import React from 'react';

import Axios from 'axios';
import debounce from 'lodash/debounce';

import API from 'utils/api';

import { UserMemberGroupRow } from 'utils/api/modules/memberGroups';

import { useStore } from 'utils/hooks/store';

export const SEARCH_THRESHOLD = 5;
export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = 10;

const SEARCH_DEBOUNCE_MS = 300;

export interface UserGroups {
  rows: UserMemberGroupRow[];
  count: number;
  totalGroups: number;
  isLoading: boolean;
  hasError: boolean;
  query: string;
  debouncedQuery: string;
  page: number;
  rowsPerPage: number;
  onQueryChange: (value: string) => void;
  onPageChange: (value: number) => void;
  onRowsPerPageChange: (value: number) => void;
  reload: () => void;
}

export const useUserGroups = (userUuid: string): UserGroups => {
  const { notification } = useStore();

  const [rows, setRows] = React.useState<UserMemberGroupRow[]>([]);
  const [count, setCount] = React.useState(0);
  // Held apart from `count` and never written from a filtered response, or the search box
  // disappears mid-typing once a query narrows the set below the threshold.
  const [totalGroups, setTotalGroups] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE);
  const [reloadToken, setReloadToken] = React.useState(0);

  // Commits the query and the page reset together, so a keystroke on page 2 doesn't fetch the
  // unfiltered first page before the debounce lands.
  const commitQuery = React.useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedQuery(value);
        setPage(0);
      }, SEARCH_DEBOUNCE_MS),
    []
  );
  React.useEffect(() => () => commitQuery.cancel(), [commitQuery]);

  React.useEffect(() => {
    const cancelTokenSource = Axios.CancelToken.source();

    const load = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const { data } = await API.memberGroups.listUserMemberGroups(
          userUuid,
          { query: debouncedQuery.trim(), limit: rowsPerPage, offset: page * rowsPerPage },
          cancelTokenSource
        )();
        setRows(data.results);
        setCount(data.count);
        if (!debouncedQuery.trim()) {
          setTotalGroups(data.count);
        }
        setHasError(false);
        if (data.results.length === 0 && data.count > 0 && page > 0) {
          setPage(0);
          return;
        }
        setIsLoading(false);
      } catch (error) {
        if (Axios.isCancel(error)) {
          return;
        }
        setHasError(true);
        setIsLoading(false);
        notification.enqueueErrorSnackbar('Could not load groups');
      }
    };

    load();

    return () => cancelTokenSource.cancel();
  }, [userUuid, debouncedQuery, page, rowsPerPage, reloadToken, notification]);

  const onQueryChange = React.useCallback(
    (value: string) => {
      setQuery(value);
      commitQuery(value);
    },
    [commitQuery]
  );

  const onRowsPerPageChange = React.useCallback((value: number) => {
    setRowsPerPage(value);
    setPage(0);
  }, []);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return {
    rows,
    count,
    totalGroups,
    isLoading,
    hasError,
    query,
    debouncedQuery,
    page,
    rowsPerPage,
    onQueryChange,
    onPageChange: setPage,
    onRowsPerPageChange,
    reload,
  };
};
